import { useEffect, useMemo, useState, type PropsWithChildren } from "react";

import clsx from "clsx";
import moment from "moment";

import { GenericTooltip } from "@components/tooltip-container";
import { PieChart, Spinner } from "@components/v2";
import { useQueryMetadata } from "@components/v2/modals";
import {
  useSalesV4Context,
  useTableRowFilters,
  useTableStateDimensions,
} from "@features/sales-v4/hooks";
import { type TableState } from "@features/sales-v4/types";
import { selectedTabToLevel } from "@features/sales-v4/utils";
import { SALES_METRICS_REPORT_V4 } from "@gql/sales-page-v4";
import { useNorthbeamQuery } from "@hooks/http";
import { useDesignElementManagerV2 } from "@hooks/use-design-element-manager";
import { useUserAttributionSettings } from "@hooks/use-user-attribution-settings";
import {
  type GetSalesMetricsReportV4,
  type GetSalesMetricsReportV4Variables,
} from "@north-beam/nb-client-side";
import { type SalesMetricRow } from "@north-beam/nb-common";
import { MetricValue } from "@shared/metric-value";
import { generateAnonBase64Hash } from "@utils/anonymous";
import { usePrefs } from "@utils/client-side-preferences";

export const Tooltip = ({
  row,
  tableState,
  children,
}: PropsWithChildren<{
  row: SalesMetricRow;
  tableState: TableState;
}>) => {
  const [isLoading, setIsLoading] = useState(true);

  return (
    <GenericTooltip
      noInfoCircle
      overlayClassName={clsx(
        "p-0 rounded-lg border-none shadow-[0_4px_8px] shadow-v2-black/15%",
      )}
      arrowClassName="bg-v2-secondary border-none"
      placement="top"
      mouseEnterDelay={500}
      content={
        <TooltipContent
          row={row}
          tableState={tableState}
          setIsLoading={setIsLoading}
        />
      }
    >
      <div
        className={clsx({
          "cursor-wait": isLoading,
          "cursor-default": !isLoading,
        })}
      >
        {children}
      </div>
    </GenericTooltip>
  );
};

const TooltipContent = ({
  row,
  tableState,
  setIsLoading,
}: {
  row: SalesMetricRow;
  tableState: TableState;
  setIsLoading: (isLoading: boolean) => void;
}) => {
  const { attributionModel, attributionWindow, accountingMode } =
    useUserAttributionSettings();
  const { dateRangeInterval } = useSalesV4Context();
  const { breakdownDimension } = useTableStateDimensions(tableState);
  const { addQueryMetadata } = useQueryMetadata();
  const { designElementManager } = useDesignElementManagerV2();

  const {
    dimensionIds,
    metricFilters,
    breakdownFilters,
    campaignFilters,
    adsetFilters,
    adFilters,
    advancedSearch,
    isSummary,
  } = useTableRowFilters({
    tableState,
    row,
  });

  const { data, loading } = useNorthbeamQuery<
    GetSalesMetricsReportV4,
    GetSalesMetricsReportV4Variables
  >(SALES_METRICS_REPORT_V4, {
    variables: {
      attributionModel,
      attributionWindow,
      accountingMode,
      dateRange: dateRangeInterval,
      level: selectedTabToLevel(tableState.selectedTab),
      timeGranularity: "daily",
      metricIds: [
        "customers",
        "customersFt",
        "customersRtn",
        "customersRtnPurchasedBeforeWindow",
        "customersRtnOnePurchase",
        "customersRtnTwoOrMorePurchases",
      ],
      dimensionIds,
      metricFilters,
      advancedSearch,
      breakdownFilters,
      campaignFilters,
      adsetFilters,
      adFilters,
      isSummary,
    },
    onCompleted(data) {
      addQueryMetadata({
        context: "Revenue",
        topic: "Customers Tooltip",
        metadata: data.me.salesMetricsReportV4.queryMetadata,
      });
    },
  });

  useEffect(() => {
    setIsLoading(loading);
  }, [setIsLoading, loading]);

  const period = useMemo(() => {
    return moment(dateRangeInterval.endDate).diff(
      dateRangeInterval.startDate,
      "days",
    );
  }, [dateRangeInterval]);

  const metricData = useMemo(() => {
    if (!data) {
      return {
        new: 0,
        returning: 0,
        purchasedInPast: 0,
        onePurchase: 0,
        twoOrMorePurchases: 0,
        newShare: 0,
        returningShare: 0,
        purchasedInPastShare: 0,
        onePurchaseShare: 0,
        twoOrMorePurchasesShare: 0,
      };
    }

    const metrics = data.me.salesMetricsReportV4.actual[0]
      .metrics as SalesMetricRow["metrics"];

    return {
      new: metrics.customersFt ?? 0,
      returning: metrics.customersRtn ?? 0,
      purchasedInPast: metrics.customersRtnPurchasedBeforeWindow ?? 0,
      onePurchase: metrics.customersRtnOnePurchase ?? 0,
      twoOrMorePurchases: metrics.customersRtnTwoOrMorePurchases ?? 0,
      newShare: (metrics.customersFt ?? 0) / (metrics.customers ?? 0),
      returningShare: (metrics.customersRtn ?? 0) / (metrics.customers ?? 0),
      purchasedInPastShare:
        (metrics.customersRtnPurchasedBeforeWindow ?? 0) /
        (metrics.customers ?? 0),
      onePurchaseShare:
        (metrics.customersRtnOnePurchase ?? 0) / (metrics.customers ?? 0),
      twoOrMorePurchasesShare:
        (metrics.customersRtnTwoOrMorePurchases ?? 0) /
        (metrics.customers ?? 0),
    };
  }, [data]);

  const name = row.name ?? breakdownDimension.getValue(row) ?? "Grand Total";

  const prefs = usePrefs();
  const isAnon = prefs.isDemoMode;

  if (loading) {
    return (
      <div
        className={clsx(
          "p-4 bg-v2-secondary rounded-lg flex items-center justify-center",
          "w-[380px] h-[330px]",
        )}
      >
        <Spinner className="text-v2-cosmic/400" />
      </div>
    );
  }

  return (
    <div className="p-4 bg-v2-secondary rounded-lg text-v2-primary">
      <div className="mb-2">
        <div className="text-sm font-semibold mb-1.5">Customers</div>
      </div>
      <div className="mb-2">
        <div className="text-xs inline-flex gap-1">
          <span>New and returning</span>
          <span className="font-semibold truncate max-w-[120px]">
            {isAnon &&
            tableState.selectedTab !== "breakdown" &&
            name !== "Grand Total"
              ? generateAnonBase64Hash({
                  value: name,
                  prefix: tableState.selectedTab,
                  doAnon: isAnon,
                })
              : name}
          </span>
          <span>customers over the past</span>
          <span>{period + 1}</span>
          <span>days</span>
        </div>
        <div className="flex items-center justify-center py-6">
          <PieChart
            data={[
              {
                label: "New",
                value: metricData.new,
                color: "#004F2D",
              },
              {
                label: "Returning",
                value: metricData.returning,
                color: "#71D5D5",
              },
            ]}
            size={160}
            showTooltip={true}
          />
        </div>
      </div>
      <div className="border border-v2-tertiary rounded bg-v2-primary">
        <div className="h-10 flex items-stretch font-bold">
          <div className="border-r border-b border-v2-tertiary px-4 flex items-center w-[200px] gap-2">
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{
                backgroundColor: designElementManager.getColorForIndex(0),
              }}
            ></span>
            New
          </div>
          <div className="flex-1 px-4 border-b border-v2-tertiary flex items-center gap-1 justify-end">
            <div
              className={clsx(
                "font-mono text-2xs rounded h-5 flex items-center px-1.5 text-nowrap",
                "text-v2-action-positive-hover bg-v2-alien/400/10%",
              )}
            >
              <MetricValue value={metricData.newShare} format="percentage" />
            </div>
            <MetricValue value={metricData.new} format="decimal" />
          </div>
        </div>
        <div className="h-10 flex items-stretch font-bold">
          <div className="border-r border-b border-v2-tertiary px-4 flex items-center w-[200px] gap-2">
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{
                backgroundColor: designElementManager.getColorForIndex(1),
              }}
            ></span>
            Returning
          </div>
          <div className="flex-1 px-4 border-b border-v2-tertiary flex items-center gap-1 justify-end">
            <div
              className={clsx(
                "font-mono text-2xs rounded h-5 flex items-center px-1.5 text-nowrap",
                "text-v2-action-positive-hover bg-v2-alien/400/10%",
              )}
            >
              <MetricValue
                value={metricData.returningShare}
                format="percentage"
              />
            </div>
            <MetricValue value={metricData.returning} format="decimal" />
          </div>
        </div>
        <div className="h-10 flex items-stretch">
          <div
            className={clsx(
              "border-r border-b border-v2-tertiary px-4 flex items-center w-[200px] gap-2",
              "pl-8",
            )}
          >
            Purchased before{" "}
            {moment(dateRangeInterval.startDate).format("M/D/Y")}
          </div>
          <div className="flex-1 px-4 border-b border-v2-tertiary flex items-center gap-1 justify-end">
            <div
              className={clsx(
                "font-mono text-2xs rounded h-5 flex items-center px-1.5 text-nowrap",
                "text-v2-action-positive-hover bg-v2-alien/400/10%",
              )}
            >
              <MetricValue
                value={metricData.purchasedInPastShare}
                format="percentage"
              />
            </div>
            <MetricValue value={metricData.purchasedInPast} format="decimal" />
          </div>
        </div>
        <div className="h-10 flex items-stretch">
          <div
            className={clsx(
              "border-r border-b border-v2-tertiary px-4 flex items-center w-[200px] gap-2",
              "pl-8",
            )}
          >
            One purchase between{" "}
            {moment(dateRangeInterval.startDate).format("M/D/Y")} -{" "}
            {moment(dateRangeInterval.endDate).format("M/D/Y")}
          </div>
          <div className="flex-1 px-4 border-b border-v2-tertiary flex items-center gap-1 justify-end">
            <div
              className={clsx(
                "font-mono text-2xs rounded h-5 flex items-center px-1.5 text-nowrap",
                "text-v2-action-positive-hover bg-v2-alien/400/10%",
              )}
            >
              <MetricValue
                value={metricData.onePurchaseShare}
                format="percentage"
              />
            </div>
            <MetricValue value={metricData.onePurchase} format="decimal" />
          </div>
        </div>
        <div className="h-10 flex items-stretch">
          <div
            className={clsx(
              "border-r border-v2-tertiary px-4 flex items-center w-[200px] gap-2 pl-8",
            )}
          >
            2+ purchases between{" "}
            {moment(dateRangeInterval.startDate).format("M/D/Y")} -{" "}
            {moment(dateRangeInterval.endDate).format("M/D/Y")}
          </div>
          <div className="flex-1 px-4 flex items-center gap-1 justify-end">
            <div
              className={clsx(
                "font-mono text-2xs rounded h-5 flex items-center px-1.5 text-nowrap",
                "text-v2-action-positive-hover bg-v2-alien/400/10%",
              )}
            >
              <MetricValue
                value={metricData.twoOrMorePurchasesShare}
                format="percentage"
              />
            </div>
            <MetricValue
              value={metricData.twoOrMorePurchases}
              format="decimal"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
